/**
 * FlexContainer Wrapper Utility
 * Wraps components in FlexContainers for responsive layouts
 */

import { Component, ResponsivePosition, ComponentType, PositioningStrategy } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { FlexContainerConfig } from '@/services/ai/ResponsiveLayoutAnalyzer';

export interface WrapperResult {
  wrappedComponents: Component[];
  containerHierarchy: Map<string, string[]>; // container ID -> child component IDs
  rootContainers: Component[];
}

export class FlexContainerWrapper {
  /**
   * Wrap components in FlexContainers based on layout analysis
   */
  public static wrapComponents(
    components: Component[],
    containers: FlexContainerConfig[],
    componentUpdates: Map<string, {
      responsivePosition: ResponsivePosition;
      parentContainerId?: string;
    }>
  ): WrapperResult {
    const wrappedComponents: Component[] = [];
    const containerHierarchy = new Map<string, string[]>();
    const rootContainers: Component[] = [];
    const processedComponents = new Set<string>();

    // Create component map for easy lookup
    const componentMap = new Map<string, Component>();
    components.forEach(comp => componentMap.set(comp.id, comp));

    // Process each container
    for (const containerConfig of containers) {
      const containerComponent = this.createContainerComponent(containerConfig);
      const childIds: string[] = [];

      // Find and process children for this container
      const childComponents = this.findContainerChildren(
        containerConfig,
        components,
        componentUpdates
      );

      childComponents.forEach(child => {
        if (!processedComponents.has(child.id)) {
          // Update child with responsive properties and set parentId
          const updates = componentUpdates.get(child.id);
          if (updates) {
            const updatedChild = {
              ...this.applyResponsiveProperties(child, updates.responsivePosition),
              parentId: containerComponent.id  // Set the parent-child relationship
            };
            wrappedComponents.push(updatedChild);
            childIds.push(updatedChild.id);
            processedComponents.add(child.id);
          }
        }
      });

      // Update container with children
      containerComponent.children = childIds;
      containerHierarchy.set(containerComponent.id, childIds);
      rootContainers.push(containerComponent);
    }

    // Process any remaining components that weren't in containers
    components.forEach(comp => {
      if (!processedComponents.has(comp.id)) {
        const updates = componentUpdates.get(comp.id);
        if (updates) {
          const updatedComp = this.applyResponsiveProperties(comp, updates.responsivePosition);
          wrappedComponents.push(updatedComp);
        } else {
          wrappedComponents.push(comp);
        }
      }
    });

    return {
      wrappedComponents,
      containerHierarchy,
      rootContainers
    };
  }

  /**
   * Create a Component from FlexContainerConfig
   */
  private static createContainerComponent(config: FlexContainerConfig): Component {
    const now = new Date();

    // Create a component that matches the actual Component interface
    const containerComponent: Component = {
      id: config.id,
      projectId: '', // Will be set by the parent component
      pageId: '', // Will be set by the parent component
      name: config.name,
      type: ComponentType.FLEX_CONTAINER,
      properties: {
        ...config.properties,
        label: config.name
      },
      position: { x: 0, y: 0, z: 0 },
      size: {
        width: config.properties.minHeight ? 800 : 600,
        height: config.properties.minHeight || 400
      },
      style: {
        display: 'flex',
        flexDirection: config.properties.flexDirection,
        justifyContent: config.properties.justifyContent as 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly',
        alignItems: config.properties.alignItems as 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch',
        flexWrap: config.properties.flexWrap,
        gap: `${config.properties.gap}px`,
        padding: `${config.properties.padding}px`,
        backgroundColor: config.properties.backgroundColor || 'transparent',
        borderRadius: config.properties.borderRadius ? `${config.properties.borderRadius}px` : '8px',
        border: '2px dashed #e2e8f0',
        minHeight: `${config.properties.minHeight || 100}px`
      },
      responsivePosition: {
        strategy: 'flex' as PositioningStrategy,
        layoutMode: 'flex',
        flexDirection: config.properties.flexDirection,
        justifyContent: config.properties.justifyContent as 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly',
        alignItems: config.properties.alignItems as 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch',
        flexWrap: config.properties.flexWrap,
        gap: config.properties.gap,
        responsiveSize: {
          width: '100%',
          height: 'auto',
          minHeight: config.properties.minHeight || 100
        },
        breakpoints: config.responsiveProperties ? {
          mobile: config.responsiveProperties.mobile ? {
            style: {
              ...config.responsiveProperties.mobile,
              gap: config.responsiveProperties.mobile.gap ? `${config.responsiveProperties.mobile.gap}px` : undefined,
              padding: config.responsiveProperties.mobile.padding ? `${config.responsiveProperties.mobile.padding}px` : undefined
            }
          } : undefined,
          tablet: config.responsiveProperties.tablet ? {
            style: {
              ...config.responsiveProperties.tablet,
              gap: config.responsiveProperties.tablet.gap ? `${config.responsiveProperties.tablet.gap}px` : undefined,
              padding: config.responsiveProperties.tablet.padding ? `${config.responsiveProperties.tablet.padding}px` : undefined
            }
          } : undefined,
          desktop: config.responsiveProperties.desktop ? {
            style: {
              ...config.responsiveProperties.desktop,
              gap: config.responsiveProperties.desktop.gap ? `${config.responsiveProperties.desktop.gap}px` : undefined,
              padding: config.responsiveProperties.desktop.padding ? `${config.responsiveProperties.desktop.padding}px` : undefined
            }
          } : undefined
        } : {}
      },
      parentId: undefined,
      children: [],
      order: 0,
      locked: false,
      hidden: false,
      version: 1,
      createdBy: 'ai-layout-assistant',
      lastModifiedBy: 'ai-layout-assistant',
      lastModifiedAt: now,
      isDeleted: false,
      deletedAt: undefined,
      createdAt: now,
      updatedAt: now
    };

    return containerComponent;
  }

  /**
   * Find children that belong to a container
   */
  private static findContainerChildren(
    containerConfig: FlexContainerConfig,
    allComponents: Component[],
    componentUpdates: Map<string, {
      responsivePosition: ResponsivePosition;
      parentContainerId?: string;
    }>
  ): Component[] {
    const children: Component[] = [];

    // Find components that belong to this container
    componentUpdates.forEach((update, componentId) => {
      if (update.parentContainerId === containerConfig.id) {
        const component = allComponents.find(c => c.id === componentId);
        if (component) {
          children.push(component);
        }
      }
    });

    return children;
  }

  /**
   * Apply responsive properties to a component
   */
  private static applyResponsiveProperties(
    component: Component,
    responsivePosition: ResponsivePosition
  ): Component {
    return {
      ...component,
      responsivePosition: {
        ...responsivePosition,
        // Ensure strategy is set
        strategy: responsivePosition.strategy || 'relative'
      },
      // Update style to be more responsive-friendly
      style: {
        ...component.style,
        // Remove absolute positioning when in a flex container
        position: responsivePosition.strategy === 'absolute' ? 'absolute' : 'relative',
        // Use responsive sizing if available
        width: responsivePosition.responsiveSize?.width || component.size.width,
        height: responsivePosition.responsiveSize?.height || component.size.height
      }
    };
  }

  /**
   * Create a nested container structure from patterns
   */
  public static createNestedStructure(
    components: Component[],
    patterns: Array<{
      type: 'row' | 'column' | 'grid';
      componentIds: string[];
      spacing: number;
    }>
  ): Component[] {
    const containerComponents: Component[] = [];
    const processedComponents = new Set<string>();

    // Sort patterns by component count (larger patterns first)
    const sortedPatterns = patterns.sort((a, b) => b.componentIds.length - a.componentIds.length);

    for (const pattern of sortedPatterns) {
      // Skip if any component is already processed
      const hasProcessed = pattern.componentIds.some(id => processedComponents.has(id));
      if (hasProcessed) continue;

      // Create container for this pattern
      const container = this.createPatternContainer(pattern, components);
      if (container) {
        containerComponents.push(container);
        pattern.componentIds.forEach(id => processedComponents.add(id));
      }
    }

    // Add unprocessed components
    const unprocessedComponents = components.filter(c => !processedComponents.has(c.id));
    containerComponents.push(...unprocessedComponents);

    return containerComponents;
  }

  /**
   * Create a container component for a pattern
   */
  private static createPatternContainer(
    pattern: {
      type: 'row' | 'column' | 'grid';
      componentIds: string[];
      spacing: number;
    },
    allComponents: Component[]
  ): Component | null {
    const childComponents = allComponents.filter(c => pattern.componentIds.includes(c.id));
    if (childComponents.length === 0) return null;

    const now = new Date();
    const containerId = uuidv4();

    // Calculate container bounds
    const minX = Math.min(...childComponents.map(c => c.position.x));
    const minY = Math.min(...childComponents.map(c => c.position.y));
    const maxX = Math.max(...childComponents.map(c => c.position.x + c.size.width));
    const maxY = Math.max(...childComponents.map(c => c.position.y + c.size.height));

    const containerComponent: Component = {
      id: containerId,
      projectId: '', // Will be set by the parent component
      pageId: '', // Will be set by the parent component
      name: `${pattern.type.charAt(0).toUpperCase() + pattern.type.slice(1)} Container`,
      type: ComponentType.FLEX_CONTAINER,
      properties: {
        flexDirection: pattern.type === 'row' ? 'row' : 'column',
        justifyContent: 'flex-start',
        alignItems: pattern.type === 'row' ? 'center' : 'stretch',
        flexWrap: pattern.type === 'grid' ? 'wrap' : 'nowrap',
        gap: pattern.spacing,
        padding: 16,
        backgroundColor: 'transparent',
        borderRadius: 8,
        minHeight: 100
      },
      position: { x: minX, y: minY, z: 0 },
      size: {
        width: maxX - minX,
        height: maxY - minY
      },
      style: {
        display: 'flex',
        flexDirection: pattern.type === 'row' ? 'row' : 'column',
        gap: `${pattern.spacing}px`,
        padding: '16px'
      },
      responsivePosition: {
        strategy: 'flex' as PositioningStrategy,
        layoutMode: 'flex',
        flexDirection: pattern.type === 'row' ? 'row' : 'column',
        justifyContent: 'flex-start',
        alignItems: pattern.type === 'row' ? 'center' : 'stretch',
        flexWrap: pattern.type === 'grid' ? 'wrap' : 'nowrap',
        gap: pattern.spacing,
        responsiveSize: {
          width: '100%',
          height: 'auto',
          minWidth: 200,
          minHeight: 100
        },
        breakpoints: {
          mobile: {
            style: {
              flexDirection: pattern.type === 'row' ? 'column' : 'column',
              padding: '12px',
              gap: `${Math.round(pattern.spacing * 0.75)}px`
            }
          },
          tablet: {
            style: {
              padding: '14px',
              gap: `${Math.round(pattern.spacing * 0.9)}px`
            }
          },
          desktop: {
            style: {
              padding: '16px',
              gap: `${pattern.spacing}px`
            }
          }
        }
      },
      parentId: undefined,
      children: pattern.componentIds,
      order: 0,
      locked: false,
      hidden: false,
      version: 1,
      createdBy: 'ai-layout-assistant',
      lastModifiedBy: 'ai-layout-assistant',
      lastModifiedAt: now,
      isDeleted: false,
      deletedAt: undefined,
      createdAt: now,
      updatedAt: now
    };

    return containerComponent;
  }

  /**
   * Optimize container hierarchy by merging similar containers
   */
  public static optimizeHierarchy(containers: Component[]): Component[] {
    // Implementation would merge containers with similar properties
    // and optimize the nesting structure
    return containers;
  }
}
